import api from '@/http/api';
import { cn, stripHtml } from '@/lib/utils';
import { useInfiniteQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useSocketStore } from '@/socketStore';
import type { NotificationType } from '@/types';
import { useAuthStore } from '@/store';
import { formatTimeInUserTimezone } from '@univdiam/ui/lib/formatedDates';
import { format } from 'date-fns';
import { Check, CheckCheck, Search, X, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import Chat from '../OrderDetail/Chat';
import { isOppositeSide } from '@univdiam/ui/lib/chatSides'

const getAllChats = (page: number, search: string, unreadOnly: boolean) => {
  return api.get(`/chat/order/all-chats?page=${page}&limit=50&search=${encodeURIComponent(search)}${unreadOnly ? '&unread=1' : ''}`);
};

type ChatData = {
  "customer_id": string,
  "customer_name": string,
  "order_id": string,
  "order_ref": string,
  "job_title": string | null
  "last_message": string | null,
  "last_chat_date": string | null
  "is_read": number | null
  "unread_count": number
}

function AllChat() {
  const queryClient = useQueryClient();
  // Read here rather than from the `user` binding further down: this is needed by
  // the socket effect below, and its dependency array is evaluated before that
  // binding is initialised.
  const viewerRole = useAuthStore((s) => s.user?.role);
  const recieveNotification = useSocketStore((s) => s.recieveNotification);

  useEffect(() => {
    const unsub = recieveNotification((data: unknown) => {
      const notif = data as NotificationType;
      if (notif.action_type === 'chat_message') {
        queryClient.invalidateQueries({ queryKey: ["all-customer-order-chats"] });
        queryClient.invalidateQueries({ queryKey: ["sidebar-chat-unread"] });
      } else if (notif.action_type === 'chat_read') {
        // Only an OPPOSITE-side read means the viewer's own messages were seen.
        // Refetch rather than guess `is_read: 1` locally: the server ticks a
        // message read only once the specific reader who claimed the
        // tick-owner slot for that order has read it (see chat_read_owner /
        // getOppositeSideWatermark, apps/api/src/orderChat/readStateService.js)
        // -- a same-side "someone else on my team read it" event or a
        // not-yet-owner reader must NOT flip the tick, and only the server
        // knows which reader that is. Also sidesteps a stale `searchQuery`
        // closure that a hand-keyed setQueryData(["...", searchQuery]) would
        // otherwise target once the user has ever searched.
        if (isOppositeSide(viewerRole, notif.action_by)) {
          queryClient.invalidateQueries({ queryKey: ["all-customer-order-chats"] });
        }
      }
    });
    return () => unsub();
  }, [recieveNotification, queryClient, viewerRole]);

  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  // WhatsApp-style "Unread" chip. Filtered server-side: the list is paged, so
  // filtering what is loaded would miss unread chats beyond the first page.
  const [unreadOnly, setUnreadOnly] = useState(false);
  const chatUnreadCount = useAuthStore((s) => s.chatUnreadCount);

  // Debounce keystrokes into the query that actually hits the server.
  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput), 200);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isPlaceholderData } = useInfiniteQuery({
    queryKey: ["all-customer-order-chats", searchQuery, unreadOnly],
    queryFn: async ({ pageParam = 1 }) => {
      return await getAllChats(pageParam as number, searchQuery, unreadOnly);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: any) => {
      const { page, totalPages } = lastPage.data.pagination;
      return page < totalPages ? page + 1 : undefined;
    },
    // Keep the old results on screen while a new search fetches, instead of
    // blanking the list on every keystroke.
    placeholderData: keepPreviousData,
  });
  const chats = data?.pages.flatMap((page: any) => page.data.orderChat) as ChatData[] || [];
  const [selectedChat, setSelectedChat] = useState<ChatData | null>(null)
  // const sendNotification = useSocketStore((s) => s.sendNotification);
  const joinOrderChat = useSocketStore((s) => s.joinOrderChat);
  const leaveOrderChat = useSocketStore((s) => s.leaveOrderChat);
  const listenForMessages = useSocketStore((s) => s.listenForMessages);
  // const { user, setNotificationCount } = useAuthStore();

  // Live messages for the currently joined room only (order_message is
  // room-scoped, unlike receive_notification). Covers exactly the gap that
  // notifications don't: this viewer's OWN sends never trigger a
  // 'chat_message' notification for themselves (getReceivers excludes the
  // actor), so without this the sidebar preview for the open chat went stale
  // until the next full reload.
  useEffect(() => {
    const unsub = listenForMessages((data: unknown) => {
      const msg = data as { orderRef: string; message: string; created_at: string };
      queryClient.setQueryData(["all-customer-order-chats", searchQuery, unreadOnly], (oldData: any) => {
        if (!oldData?.pages) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page: any) => ({
            ...page,
            data: {
              ...page.data,
              orderChat: page.data.orderChat.map((c: any) =>
                String(c.order_id) === String(msg.orderRef)
                  ? { ...c, last_message: msg.message, last_chat_date: msg.created_at }
                  : c
              )
            }
          }))
        };
      });
    });
    return () => unsub();
  }, [listenForMessages, queryClient, searchQuery, unreadOnly]);

  // Room membership follows the selected chat, including when it is cleared by
  // the back button or when this page unmounts. The socket store remembers
  // joined rooms so it can restore them after a reconnect, so an id left behind
  // here is re-joined on every future reconnect for the rest of the session.
  useEffect(() => {
    if (!selectedChat?.order_ref) return;
    const orderRef = selectedChat.order_ref;
    joinOrderChat(orderRef);
    return () => {
      leaveOrderChat(orderRef);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChat?.order_ref]);

  const handleSelectChat = (chat: ChatData) => {
    setSelectedChat(chat);
    // Optimistically clear unread count for this chat
    queryClient.setQueryData(["all-customer-order-chats", searchQuery, unreadOnly], (oldData: any) => {
      if (!oldData?.pages) return oldData;
      return {
        ...oldData,
        pages: oldData.pages.map((page: any) => ({
          ...page,
          data: {
            ...page.data,
            orderChat: page.data.orderChat.map((c: any) =>
              String(c.order_id) === String(chat.order_id)
                ? { ...c, unread_count: 0 }
                : c
            )
          }
        }))
      };
    });
    // No optimistic write to ["sidebar-chat-unread"]: it holds a NUMBER of chats
    // with unread, not a list and not this chat's message count. <Chat>
    // invalidates the key once the mark-read POST lands, so it stays server truth.
    // The Chat component will handle marking messages as read when it mounts
  };
  // Search is now purely on backend

  return (
    <div className="p-3 lg:p-6 bg-card text-card-foreground flex h-full w-full flex-col lg:flex-row gap-6">
      <div className={cn("bg-card text-card-foreground flex h-full min-h-0 flex-col gap-6 rounded-xl border py-6 w-full pb-0 lg:w-96 shrink-0", selectedChat && 'hidden lg:flex')}>
        {/* Header */}
        <div className="grid auto-rows-min grid-cols-[1fr_auto] items-start gap-1.5 px-6">
          <h2 className="font-display font-semibold text-xl lg:text-2xl">Chats</h2>
        </div>

        {/* Search */}
        <div className="relative px-6">
          <Search
            width="24"
            height="24"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-search text-muted-foreground absolute left-10 top-1/2 size-4 -translate-y-1/2"
          />
          <input
            type="text"
            placeholder="Search..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="border-input file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 ps-10 text-base shadow-xs outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
          />
          {isPlaceholderData ? (
            <Loader2 className="absolute right-8 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 animate-spin" />
          ) : (
            searchInput !== '' &&
            <X className="absolute right-8 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 cursor-pointer" onClick={() => {
              setSearchInput('');
            }} />
          )}
        </div>

        {/* Filter */}
        <div className="-mt-3 flex gap-1.5 px-6">
          {([["All", false], ["Unread", true]] as const).map(([label, value]) => (
            <button
              key={label}
              type="button"
              aria-pressed={unreadOnly === value}
              onClick={() => setUnreadOnly(value)}
              className={cn(
                "rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors cursor-pointer",
                unreadOnly === value ? "bg-[#883734]/10 text-[#883734]" : "text-muted-foreground hover:bg-muted"
              )}
            >
              {label}
              {value && chatUnreadCount > 0 && <span className="ms-1 opacity-70">{chatUnreadCount}</span>}
            </button>
          ))}
        </div>

        {/* Chat List */}
        <div className="flex-1 min-h-0 overflow-auto p-0" onScroll={(e) => {
          const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
          if (scrollHeight - scrollTop <= clientHeight + 50) {
            if (hasNextPage && !isFetchingNextPage) fetchNextPage();
          }
        }}>
          {unreadOnly && data && chats.length === 0 && (
            <p className="px-6 py-8 text-center text-xs text-muted-foreground">No unread chats</p>
          )}
          <div className="block min-w-0 divide-y">
            {chats?.map((chat) => (
              <div
                key={chat.order_id}
                className={cn("group/item hover:bg-muted relative flex min-w-0 cursor-pointer items-start gap-4 px-6 py-4", chat.order_id === selectedChat?.order_id && 'bg-[#883734]/10')}
                onClick={() => handleSelectChat(chat)}
              >

                {/* Chat Info */}
                <div className="min-w-0 grow space-y-1">
                  {/* Row 1: Order Name (primary) + timestamp */}                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="truncate text-sm font-semibold text-foreground">
                          {chat.job_title}
                        </span>
                        <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                          #{chat.order_ref}
                        </span>
                      </div>
                      {
                        chat.last_chat_date && <span className="text-muted-foreground flex-none text-[11px]">
                          {format(new Date(chat.last_chat_date), 'dd MMM')} • {formatTimeInUserTimezone(chat.last_chat_date)}
                        </span>
                      }
                    </div>

                  {/* Row 2: Last message + unread badge */}
                  {
                    chat.last_chat_date && <div className="flex items-center gap-1.5 min-w-0">
                      {
                        chat.is_read ? <CheckCheck
                          strokeWidth={2}
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="lucide lucide-check-check h-3.5 w-3.5 shrink-0 text-green-500"
                          size={24}
                        /> : <Check
                          width="24"
                          height="24"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="lucide lucide-check text-muted-foreground h-3.5 w-3.5 shrink-0"
                        />
                      }

                      <span className="text-muted-foreground truncate text-start text-xs flex-1 min-w-0">
                        {stripHtml(chat.last_message ?? undefined) || "Attachment"}
                      </span>

                      {chat.unread_count > 0 && (
                        <div className="ms-auto flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green-500 text-[10px] font-medium text-white">
                          {chat.unread_count}
                        </div>
                      )}
                    </div>
                  }
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
      <div className={cn("h-full w-full min-h-0 min-w-0 flex-1", !selectedChat && 'hidden lg:block')}>
        {
          selectedChat ? <div className='h-full min-w-0'>
            <Chat 
              orderDetailData={{
                order_id: selectedChat.order_ref,
                id: selectedChat.order_id
              }} 
              onBack={() => setSelectedChat(null)}
            />
          </div> : <p className="text-xl w-full h-full flex items-center justify-center">Please select chat</p>
        }
      </div>
    </div>

  );
}

export default AllChat